import React, { Component } from 'react';

import cx from 'classnames';
import scroll from 'scroll';
import scrollDoc from 'scroll-doc';
import _memoize from 'lodash.memoize';
import ProgressBar from './ProgressBar';
import ActionButton from '../../ActionButton';
import { DEFAULT, CONTENT, BACK_BTN } from '../styles/wizard';

const scrollElement = scrollDoc();


























function scrollToTop() {
  scroll.top(scrollElement, 0);
}

/**
 * Wizard React component. Display an animated progress bar and automatically handles going
 * back to previous stages. Each child component will receive a function prop, 'goToNextWizardStage'
 * which will allow them to progress the wizard to the next stage.
 *
 * @param {WizardProps} props
 *
 * @return {React.Element}
 */
export default class Wizard extends Component {
  /**
   * Loops through children stages and injects the functionality to move this
   * parent component to the next stage. This allows them to handle their own submit
   * and create their own continue/submit buttons
   *
   * @param {React.Element | Array<React.Element>} children
   *
   * @return {React.Element | Array<React.Element>}
   */
  addNavigationToChildren = _memoize(
    ({ children }) => {
      const props = {
        goToNextWizardStage: this.goToNextStage,
        goToPreviousWizardStage: this.goToPreviousStage
      };

      if (!Array.isArray(children)) {
        return React.cloneElement(children, props);
      }

      const filteredChildren = children.filter((child) => child !== null);

      return filteredChildren.map((child) => React.cloneElement(child, props));
    }
  );

  static defaultProps = { initialStage: 1, progressBarPosition: 'right', backButtonAtTop: false };

  constructor(props) {
    super(props);

    scrollToTop();

    this.state = {
      totalStages: props.children.length || 1,
      stage: props.initialStage,
      displayStage: 0
    };
  }

  /**
   * Add a timeout here to ensure initial progress bar styles are loaded
   * so we can animate to the inital display stage. The cxs styles are applied in
   * the next process 'tick' so an empty set timeout will mitigate this.
   *
   * @return {void}
   */
  componentDidMount() {
    setTimeout(() => {
      const { onReady } = this.props;

      this.setState(({ stage }) => ({ displayStage: stage }));

      if (onReady) {
        onReady({
          goToPreviousWizardStage: this.goToPreviousStage,
          goToNextWizardStage: this.goToNextStage
        });
      }
    }, 0);
  }

  /**
   * Adds one to the stage index to move to the next stage. If we're on the last
   * stage then we do nothing.
   *
   * @return {void}
   */
  goToNextStage = () => {
    const { stage: currentStage, totalStages } = this.state;

    if (currentStage === totalStages) {return;}

    this.setState(({ stage }) => ({
      stage: stage + 1,
      displayStage: stage + 1
    }), scrollToTop);
  };

  /**
   * Goes to the previous stage by lowering the stage index by one
   *
   * @return {void}
   */
  goToPreviousStage = () => {
    this.setState(({ stage }) => ({
      stage: stage - 1,
      displayStage: stage - 1
    }), this.handleBack);
  };

  /**
   * Call an onBack callback if it exists in props with the new stage
   *
   * @return {void}
   */
  handleBack() {
    const { onBack } = this.props;
    const { stage } = this.state;

    if (!onBack) {
      scrollToTop();

      return;
    }

    onBack({ stage });

    scrollToTop();
  }

  render() {
    const { stage, displayStage, totalStages } = this.state;
    const {
      hideBack,
      hideProgressBar,
      fullWidth,
      className,
      isV2Design,
      progressBarPosition,
      backButtonAtTop } = this.props;
    const children = this.addNavigationToChildren(this.props);

    return (
      <div
        className={cx(
          DEFAULT({
            fullWidth
          }),
          'wizard',
          className
        )}>

        {backButtonAtTop &&
        <ActionButton
          labelText="Back"
          icon="returnI"
          onClick={this.goToPreviousStage}
          visible={!hideBack && stage !== 1}
          className={BACK_BTN}
          iconSize={15} />}


        {!hideProgressBar &&
        <ProgressBar
          progressBarPosition={progressBarPosition}
          isV2Design={isV2Design}
          currentStage={displayStage}
          maxStage={totalStages} />}


        {!backButtonAtTop &&
        <ActionButton
          labelText="Back"
          icon="returnI"
          onClick={this.goToPreviousStage}
          visible={!hideBack && stage !== 1}
          className={BACK_BTN}
          iconSize={15} />}


        <div className={cx(CONTENT({ hideProgressBar }), 'wizard__content')}>
          {children[stage - 1] || children}
        </div>
      </div>);

  }
}